
import { Link, useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
  import * as cam from "@mediapipe/camera_utils";
import { FaceMesh } from "@mediapipe/face_mesh";
import { routes } from '../routes/routes-names';
import moment from "moment";
import { eventService } from "../lib/api/event";
import "bootstrap/dist/css/bootstrap.min.css";
import Swal from "sweetalert2";

const DailyPhotoCapture = () => {
  const { state } = useLocation();
  const { doctorId,doctorName,eventId,day } = state || {};

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cameraRef = useRef<cam.Camera | null>(null);
  const streamRef = useRef<MediaStream | null>(null);


  const [captured, setCaptured] = useState<string | null>(null);
  const [status, setStatus] = useState("Initializing camera...");
  const [isBlinking, setIsBlinking] = useState(false);
  const [latitude, setLatitude] = useState<number>(0);
  const [longitude, setLongitude] = useState<number>(0);
  const [lastBlink, setLastBlink] = useState<number>(0);
  const [photoReady, setPhotoReady] = useState(false);
  const [loading, setLoading] = useState(false);
  
    const eyeClosedFramesRef = useRef(0);
    const blinkDetectedRef = useRef(false);
    const liveFramesRef = useRef(0);
    const startTimeRef = useRef(Date.now());
  const navigate = useNavigate();

  const getLocation = () => {

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log("Latitude:", position.coords.latitude);
          console.log("Longitude:", position.coords.longitude);
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
          // Combine this data with your captured photo
        },
        (error) => {
          console.error("Error getting location:", error);
        }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  };

  useEffect(() => {
      const init = async () => {
        try {
          streamRef.current = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "user" },
            audio: false,
          });
  
          if (!videoRef.current) return;
          videoRef.current.srcObject = streamRef.current;
          await videoRef.current.play();
  
          const faceMesh = new FaceMesh({
            locateFile: (file) =>
              `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`,
          });
  
          faceMesh.setOptions({
            maxNumFaces: 1,
            refineLandmarks: true,
            minDetectionConfidence: 0.6,
            minTrackingConfidence: 0.6,
          });
  
          faceMesh.onResults(onResults);
  
          cameraRef.current = new cam.Camera(videoRef.current, {
            onFrame: async () => {
              if (videoRef.current) {
                await faceMesh.send({ image: videoRef.current });
              }
            },
            width: 640,
            height: 480,
          });
  
          await cameraRef.current.start();
        } catch {
          Swal.fire("Error", "Camera permission denied", "error");
        }
      };
  
      init();
      getLocation();
      return stopCamera;
    }, []);
  
    // ---------------- FACE PROCESSING ----------------
    const onResults = (results: any) => {
      if (captured) return;
  
      if (!results.multiFaceLandmarks?.length) {
        setStatus("Face not detected");
        liveFramesRef.current = 0;
        return;
      }
  
      if (results.multiFaceLandmarks.length !== 1) {
        setStatus("Only one face allowed");
        liveFramesRef.current = 0;
        return;
      }
  
      const landmarks = results.multiFaceLandmarks[0];
      const nose = landmarks[1];
  
      // ---- Depth check (blocks photo / screen)
      if (!hasDepthVariation(landmarks)) {
        setStatus("Move closer to camera");
        liveFramesRef.current = 0;
        return;
      }
  
      // ---- Face roughly centered
      if (nose.x < 0.2 || nose.x > 0.8) {
        setStatus("Center your face");
        liveFramesRef.current = 0;
        return;
      }
  
      // ---- Live tracking counter
      liveFramesRef.current++;
  
      // ---- Blink detection (soft)
      const ear = getEyeAspectRatio(landmarks);
      if (ear < 0.23) {
        eyeClosedFramesRef.current++;
      }
  
      if (eyeClosedFramesRef.current >= 2) {
        blinkDetectedRef.current = true;
      }
  
      // ---- UX message
      if (!blinkDetectedRef.current) {
        setStatus("Please blink your eyes");
      } else {
        setStatus("Hold steady…");
      }
  
      // ---- FINAL CAPTURE CONDITION (NO DEADLOCK)
      const trackedLongEnough =
        liveFramesRef.current > 60; // ~2 seconds @30fps
  
      const fallbackTimeout =
        Date.now() - startTimeRef.current > 5000;
  
      if (
        hasDepthVariation(landmarks) &&
        (blinkDetectedRef.current || fallbackTimeout) &&
        trackedLongEnough
      ) {
        capturePhoto();
      }
    };
  
    // ---------------- CAPTURE ----------------
    const capturePhoto = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas) return;
  
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
  
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
  
      ctx.drawImage(video, 0, 0);
      const img = canvas.toDataURL("image/jpeg");
  
      setCaptured(img);
      setStatus("Live photo captured");
      stopCamera();
    };
  
    // ---------------- STOP CAMERA ----------------
    const stopCamera = () => {
      cameraRef.current?.stop();
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
    const resetCapture = () => {
      setCaptured(null);
      setPhotoReady(false);
      setStatus("Ready — Please blink again to capture a new photo.");
    };
  
    const uploadPhoto = async () => {
      if (!captured){ 
         Swal.fire({
          title: "Error",
          text: "Photo not captured",
          icon: "error",
          confirmButtonText: "OK",
        })
        return;
      }
      setStatus("Uploading...");
      setLoading(true);
  
      try {
        const blob = await (await fetch(captured)).blob();
        const photData = new FormData();
        photData.append("photo", blob, "blink.jpg");
        const doctorregInfo = {
          eventId: eventId,
          doctorId: doctorId,
          doctorName:doctorName ,
          latitude: latitude,
          longitude: longitude,
          createdon: moment().format('YYYY-MM-DD'),
          posttime: moment().format('h:mm:ss'),
        }
        photData.append("doctorregInfo", JSON.stringify(doctorregInfo));
  
        const { success, message, data } = await eventService.saveDoctorPhoto(photData);
        if (success) {
          setLoading(false);
          Swal.fire({
          title: "success",
          text: message,
          icon: "success",
          confirmButtonText: "OK",
        });
        
        }else{
          Swal.fire({
          title: "Error",
          text: message,
          icon: "error",
          confirmButtonText: "OK",
        });

        }
        navigate(routes.eventDocInfo);
      } catch (err) {
        alert(err);
        setLoading(false);
        Swal.fire({
          title: "Error",
          text: "Upload Failed",
          icon: "error",
          confirmButtonText: "OK",
        });
        setStatus("Upload failed. Check backend.");
      }
    };
  return (
    <div className="container mt-4">
      {/* Later: add camera logic here */}
        <div className="card shadow-lg p-4">
        <h3 className="text-center mb-4 cme-header fw-bold">
           Doctor photo capture for <p><b>Day:</b> {day}</p>
           <p>Doctor Name:{doctorName}</p>
        </h3>

        <div className="row">
          {/* LEFT SIDE: Webcam Capture */}
          <div className="col-md-5 d-flex flex-column align-items-center border-end">
            <div style={{ padding: 0, textAlign: "center" }}>


              {!captured && (
                <div>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    width={320}
                    height={320}
                    style={{ border: "2px solid #ccc", borderRadius: 8 }}
                  />
                  <p style={{ fontWeight: "bold", color: "green" }}>{status}</p>
                  <button
                    className="btn btn-warning"
                    onClick={capturePhoto}
                  >
                    Capture Manually
                  </button>
                </div>
              )}
              <canvas ref={canvasRef} style={{ display: "none" }} />

              {captured && (
                <div style={{ marginTop: 0 }}>
                  <h4>Captured Photo:</h4>
                  <img
                    src={captured}
                    alt="Captured"
                    style={{
                      width: "100%",
                      maxWidth: 300,
                      borderRadius: 8,
                      border: "2px solid #888",
                    }}
                  />
                  <div style={{ marginTop: 15, display: "flex", gap: "10px", justifyContent: "center" }}>
                    <button
              onClick={uploadPhoto}
              style={{
                padding: "10px 20px",
                backgroundColor: "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: "1em",
              }}
            >
              Submit
            </button> 
                    <button
                      onClick={resetCapture}
                      style={{
                        padding: "10px 20px",
                        backgroundColor: "#f44336",
                        color: "#fff",
                        border: "none",
                        borderRadius: 6,
                        cursor: "pointer",
                        fontSize: "1em",
                      }}
                    >
                      Recapture
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
          </div>
          </div>
    </div>
  );
};

function getEyeAspectRatio(landmarks: any[]): number {
  const top = landmarks[159];
  const bottom = landmarks[145];
  const left = landmarks[33];
  const right = landmarks[133];

  const vertical = Math.hypot(top.x - bottom.x, top.y - bottom.y);
  const horizontal = Math.hypot(left.x - right.x, left.y - right.y);
  return vertical / horizontal;
}
export default DailyPhotoCapture;

// ---------------- UTILS ----------------

function hasDepthVariation(lm: any[]) {
  const z = lm.map((p) => p.z);
  return Math.abs(Math.max(...z) - Math.min(...z)) > 0.01;
}
